import React from "react";
import { useGetLegalDates } from "../../api/queries";

interface EULAContentProps {
  showLastUpdated?: boolean;
}

const EULAContent: React.FC<EULAContentProps> = ({
  showLastUpdated = true,
}) => {
  const { data: legalDates } = useGetLegalDates();

  const eulaLastUpdated = legalDates?.eulaLastUpdated
    ? new Date(legalDates.eulaLastUpdated).toLocaleDateString()
    : "Loading...";

  return (
    <>
      {showLastUpdated && (
        <p className="text-sm text-gray-600 mb-6">
          Last updated: {eulaLastUpdated}
        </p>
      )}

      <div className="space-y-4 text-gray-700">
        <p className="text-sm">
          This End User License Agreement (&quot;EULA&quot;) is a legally
          binding agreement between you (&quot;You&quot;) and The Arizona Board
          of Regents for and on behalf of Arizona State University
          (&quot;Provider&quot;). This EULA governs your use of PSYCH FOR LIFE
          <sup>®</sup>
          Application (including all related documentation, the
          &quot;App&quot;). The App is licensed, not sold, to you. This EULA is
          effective as of the date you access the App.
        </p>

        <p className="text-sm font-semibold bg-gray-50 p-3 rounded border-l-4 border-maroon-500">
          BY CLICKING THE &quot;AGREE&quot; BUTTON OR BOX, YOU (A) ACKNOWLEDGE
          THAT YOU HAVE READ AND UNDERSTAND THIS EULA; (B) REPRESENT THAT YOU
          ARE OF LEGAL AGE TO ENTER INTO A BINDING AGREEMENT; (C) ACCEPT THIS
          EULA AND AGREE THAT YOU ARE LEGALLY BOUND BY ITS TERMS. IF YOU DO NOT
          AGREE TO THESE TERMS, DO NOT USE THE APPLICATION.
        </p>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            1. Description
          </h2>
          <p>
            The App is a software application, proprietary to Provider, which is
            designed to allow You to use Provider&apos;s services to which the
            App relates (&quot;Services&quot;).
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            2. License Grant
          </h2>
          <p>
            Subject to the restrictions set forth in this EULA, Provider grants
            you a non-exclusive, revocable, non-transferable, and
            non-sublicensable license to use the App only in the United States
            solely for your personal non-commercial purposes in connection with
            the Services.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            3. License Restrictions
          </h2>
          <p className="mb-2">
            Except as may be expressly permitted by applicable law or expressly
            authorized by the App, you shall not:
          </p>
          <ol className="list-[lower-alpha] pl-8 space-y-2 marker:font-normal">
            <li>copy the App;</li>
            <li>modify or otherwise create derivative works of the App;</li>
            <li>
              reverse engineer, disassemble, decompile, decode, or otherwise
              attempt to derive or gain access to the source code of the App or
              any part thereof;
            </li>
            <li>
              remove, delete, alter, or obscure any trademarks or any copyright,
              trademark, patent, or other intellectual property or proprietary
              rights notices from the App, including any copy thereof;
            </li>
            <li>
              distribute, transfer, sublicense or otherwise make available the
              App, or any features or functionality of the App, to any third
              party;
            </li>
            <li>
              use the App in any manner that could disable, overburden, damage,
              or impair the App or interfere with any other party&apos;s use of
              the App; or
            </li>
            <li>
              remove, disable, circumvent, or otherwise create or implement any
              workaround to any copy protection, rights management, or security
              features in or protecting the App.
            </li>
          </ol>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            4. Reservation of Rights
          </h2>
          <p>
            You acknowledge and agree that the App is provided under license,
            and not sold, to you. You do not acquire any ownership interest in
            the App under this EULA, or any other rights thereto other than to
            use the App in accordance with the license granted, and subject to
            all terms, conditions, and restrictions, under this EULA. Provider
            reserves and shall retain its entire right, title, and interest in
            and to the App, including all copyrights, trademarks, and other
            intellectual property rights, except as expressly granted to you in
            this EULA.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            5. Terms &amp; Conditions
          </h2>
          <p>
            By using the App, you agree to the policies governing your use of
            the Services including Provider Terms &amp; Conditions (available at{" "}
            <a
              href="https://psychforlife.org/terms-of-service"
              className="text-blue-500 hover:text-blue-600 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://psychforlife.org/terms-of-service
            </a>
            ) as updated from time to time.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            6. Collection and Use of Your Information
          </h2>
          <p>
            You acknowledge that when you use the App, Provider may collect
            certain data and information about your use of the App and Services.
            By using the App, you consent to the collection, use, sharing, and
            transfer of your data, as outlined in the Privacy Policy (available
            at{" "}
            <a
              href="https://psychforlife.org/privacy-policy"
              className="text-blue-500 hover:text-blue-600 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://psychforlife.org/privacy-policy
            </a>
            ) as updated from time to time.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            7. No Included Maintenance and Support
          </h2>
          <p>
            Provider may deploy changes, updates, or enhancements to the App at
            any time. Provider may provide maintenance and support for the App
            but has no obligation whatsoever to furnish such maintenance or
            support services to You and may terminate such maintenance or
            support services at any time without notice.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            8. Warranty Disclaimer
          </h2>
          <p className="uppercase font-semibold text-sm leading-relaxed mb-3">
            YOUR USE OF THE APP IS AT YOUR SOLE RISK. THE APP IS PROVIDED ON AN
            &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; BASIS. TO THE MAXIMUM
            EXTENT PERMITTED UNDER APPLICABLE LAW, PROVIDER EXPRESSLY DISCLAIMS
            ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING
            THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
            PURPOSE AND NON-INFRINGEMENT AND WARRANTIES THAT MAY ARISE OUT OF
            COURSE OF DEALING, COURSE OF PERFORMANCE, USAGE, OR TRADE PRACTICE.
          </p>
          <p>
            Provider is solely responsible for any warranties, whether express
            or implied by law to the effect not effectively disclaimed under
            this Section.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            9. Medical Disclaimer
          </h2>
          <p className="uppercase font-semibold text-sm leading-relaxed">
            THE APP IS PROVIDED FOR INFORMATIONAL PURPOSES ONLY AND IS NOT
            INTENDED, DESIGNED, OR IMPLIED TO DIAGNOSE, PREVENT, OR TREAT ANY
            CONDITION OR DISEASE, WHETHER MENTAL HEALTH OR OTHERWISE, OR TO BE A
            SUBSTITUTE FOR PROFESSIONAL MEDICAL OR MENTAL HEALTH CARE. PROVIDER
            IS NOT YOUR HEALTHCARE PROVIDER. PROVIDER IS NOT AND DOES NOT
            PROVIDE 911 OR OTHER EMERGENCY SERVICES AND MAY NOT CONTACT YOU OR
            ANYONE ON YOUR BEHALF WITH RESPECT TO YOUR MEDICAL CONDITION OR
            TREATMENT. IF YOU ARE EXPERIENCING A MENTAL HEALTH OR OTHER MEDICAL
            EMERGENCY, PLEASE CONTACT 911 OR GO TO THE NEAREST EMERGENCY ROOM.
            YOU SHOULD ALWAYS CONSULT A MEDICAL PROFESSIONAL IF YOU HAVE ANY
            QUESTIONS REGARDING A MEDICAL CONDITION. YOU SHOULD NEVER DISREGARD
            PROFESSIONAL MEDICAL ADVICE OR DELAY IN SEEKING IT BECAUSE OF
            SOMETHING YOU HAVE READ OR RECEIVED USING THE APP. PROVIDER IS NOT
            RESPONSIBLE FOR THE ACCURACY, RELIABILITY, EFFECTIVENESS, OR CORRECT
            USE OF INFORMATION YOU RECEIVE THROUGH OR AS GENERATED BY THE APP.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            10. Limitation of Liability
          </h2>
          <p className="uppercase font-semibold text-sm leading-relaxed">
            IN NO EVENT WILL PROVIDER BE LIABLE UNDER OR IN CONNECTION WITH THIS
            EULA UNDER ANY LEGAL OR EQUITABLE THEORY, FOR ANY: (a)
            CONSEQUENTIAL, INCIDENTAL, INDIRECT, EXEMPLARY, SPECIAL, ENHANCED,
            OR PUNITIVE DAMAGES; (b) INCREASED COSTS, DIMINUTION IN VALUE OR
            LOST BUSINESS, PRODUCTION, REVENUES, OR PROFITS; (c) LOSS OF
            GOODWILL OR REPUTATION; (d) USE, INABILITY TO USE, LOSS,
            INTERRUPTION, DELAY, OR RECOVERY OF ANY DATA, OR BREACH OF DATA OR
            SYSTEM SECURITY; OR (e) COST OF REPLACEMENT GOODS OR SERVICES, IN
            EACH CASE REGARDLESS OF WHETHER PROVIDER WAS ADVISED OF THE
            POSSIBILITY OF SUCH LOSSES OR DAMAGES OR SUCH LOSSES OR DAMAGES WERE
            OTHERWISE FORESEEABLE. IN NO EVENT WILL PROVIDER&apos;S AGGREGATE
            LIABILITY ARISING OUT OF OR RELATED TO THIS EULA UNDER ANY LEGAL OR
            EQUITABLE THEORY, EXCEED THE AMOUNT OF SUBSCRIPTION FEE ACTUALLY
            PAID TO THE PROVIDER BY YOU DURING THE TWELVE (12) MONTHS PRIOR TO
            THE ACTION GIVING RISE TO SUCH DAMAGES, LOSSES, CLAIMS OR CAUSES OF
            ACTION.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            11. Third-Party Claims
          </h2>
          <p>
            Subject to Sections 10, 18 and 19 of this EULA, Provider is
            responsible for addressing any claims of yours or any third-party
            relating to the App or your possession and/or use of the App,
            including: (i) product liability claims; (ii) any claim that the App
            fails to conform to any applicable legal or regulatory requirement;
            and (iii) claims arising under consumer protection, privacy, or
            similar legislation. Subject to Sections 10, 18, and 19 of this
            EULA, in the event of a third-party claim that the App, or your
            possession and use of the App, infringes third party&apos;s
            intellectual property rights, Provider will be solely responsible
            for the investigation, defense, settlement and discharge of any such
            intellectual property claim.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            12. Termination
          </h2>
          <p className="mb-3">
            You may terminate the license to use this App by deleting your
            account on the App. Provider may terminate your license to use the
            App if you violate this EULA, in Provider&apos;s sole discretion
            without notice to you. Upon termination, you must cease all use of
            the App. The termination of the license to use this App for any
            reason shall not affect terms of this EULA which by their nature
            continue and survive, including without limitation terms regarding
            ownership, limitations of liability, dispute resolution, governing
            law, and disclaimers.
          </p>
          <p>
            For the avoidance of doubt, Provider may change or discontinue any
            or all or any parts of the App, at any time and without notice, at
            Provider&apos;s sole discretion, for any purpose, including to make
            improvements, address technical needs, and help prevent damage to
            users or the App. To the extent permitted by applicable laws,
            Provider is not responsible for any loss or harm related to your
            inability to access or use the App.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            13. Legal Compliance
          </h2>
          <p>
            You represent and warrant that you (i) are not located in a country
            that is subject to a U.S. Government embargo, or that has been
            designated by the U.S. Government as a &quot;terrorist
            supporting&quot; country; and (ii) are not listed on any U.S.
            Government list of prohibited or restricted parties.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            14. Name and Address
          </h2>
          <p>
            If you have any questions, complaints, or claims with respect to the
            App, they should be directed to us by email at{" "}
            <a
              href="mailto:support@psychforlife.org"
              className="text-blue-500 hover:text-blue-600 underline"
            >
              support@psychforlife.org
            </a>
            .
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            15. Third-Party Beneficiaries
          </h2>
          <p>
            Nothing in this EULA, express or implied, is intended or will be
            construed to confer upon any person or entity, other than You, any
            right, remedy or claim under or by reason of this EULA.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            16. Governing Law
          </h2>
          <p>
            Any dispute regarding or arising under this EULA, or the
            interpretation of this EULA, will be subject to and resolved in
            accordance with the laws of the State of Arizona, without regard to
            its conflicts of laws principles. Any legal suit, action, or
            proceeding arising out of, or related to, this EULA shall be
            instituted exclusively in the Superior Court of Arizona in Maricopa
            County, and any appellate court thereof. You waive any and all
            objections to the exercise of jurisdiction over you by such courts
            and to venue in such courts.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            17. Dispute Resolution
          </h2>
          <p>
            In the event of any dispute, claim, question, or disagreement
            arising from or relating to this EULA or the breach, termination or
            validity thereof, Provider and You will first attempt to resolve the
            matter over a period of at least thirty (30) calendar days before
            resorting to formal dispute resolution, except that equitable
            remedies may be sought immediately. To this effect, the parties will
            consult and negotiate with each other in good faith and, recognizing
            their mutual interests, attempt to reach a just and equitable
            solution satisfactory to both parties. The parties agree to
            arbitrate disputes filed in Arizona Superior Court that are subject
            to mandatory arbitration pursuant to Arizona Revised Statutes A.R.S.
            § 12-133. A.R.S. § 12-1518 requires this provision in all Arizona
            State University contracts.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            18. Indemnification and Liability Limitations
          </h2>
          <p>
            Because Provider is a public institution, any indemnification,
            liability limitation, releases, or hold harmless provisions are
            limited as required by Arizona law, including Article 9, Sections 5
            and 7 of the Arizona Constitution and ARS §§ 35-154 and 41-621.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            19. Non-Waiver of Sovereign Immunity
          </h2>
          <p>
            Notwithstanding anything to the contrary in this EULA, Provider
            expressly retains all rights and benefits of sovereign immunity and
            nothing in this EULA shall be a deemed as a waiver of sovereign
            immunity or as increasing Provider&apos;s liability beyond any
            statutory limitation of liability. Nothing in this EULA shall inure
            to the benefit of any third party for the purpose of allowing any
            claim against Provider which would otherwise be barred under the
            doctrine of sovereign immunity or operation of law.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            20. Severability
          </h2>
          <p>
            If any provision of this EULA shall for any reason be found invalid,
            illegal, unenforceable, or in conflict with any valid controlling
            law: (a) such provision will be separated from this EULA; (b) such
            invalidity, illegality, unenforceability, or conflict will not
            affect any other provision hereof; and (c) this EULA will be
            interpreted and construed as if such provision, to the extent the
            same shall have been held invalid, illegal, unenforceable, or in
            conflict, had never been contained herein.
          </p>
        </section>

        <div className="mt-8 pt-6 border-t border-gray-300">
          <p className="text-sm text-gray-600">
            If you have any questions regarding this End User License Agreement,
            please contact us at{" "}
            <a
              href="mailto:support@psychforlife.org"
              className="text-blue-500 hover:text-blue-600 underline"
            >
              support@psychforlife.org
            </a>
            .
          </p>
        </div>
      </div>
    </>
  );
};

export default EULAContent;
